import * as React from 'react'
import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { LoaderCircle, UserIcon } from 'lucide-react'

import { getFilterOptionUsers, type UserFilterOption } from '@/api/teamInsights'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandInput, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface UserFilterProps {
  enterpriseId: string | null
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  // Applied to the trigger Button — lets parents stretch the pill to fill a grid cell.
  triggerClassName?: string
}

const PAGE_SIZE = 50

const renderUserLabel = (user: UserFilterOption) => user.name || user.email || user.user_id

const UserFilter = ({ enterpriseId, value, onChange, disabled = false, triggerClassName }: UserFilterProps) => {
  const queryClient = useQueryClient()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  // Cache labels for selected users so the trigger label and rendered selections
  // survive a search that narrows the typeahead away from a currently-checked user.
  // This popover is typeahead-paginated and infinite-scrolled — a selected user
  // can disappear from the rendered list mid-session as the search query changes
  // or before the next page loads, so we need this local cache to keep the
  // single-select label correct. Don't "simplify" it down to the AppFilter pattern
  // without first switching to a non-paginated user list.
  const [selectedLabels, setSelectedLabels] = React.useState<Record<string, UserFilterOption>>({})

  React.useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(handle)
  }, [search])

  const { data, isLoading, isError, isPlaceholderData, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['team-insights', 'filter-options', 'users', enterpriseId, debouncedSearch],
      queryFn: ({ pageParam }) =>
        getFilterOptionUsers(enterpriseId!, debouncedSearch || undefined, PAGE_SIZE, pageParam).then((r) => r.data),
      enabled: open && !!enterpriseId,
      initialPageParam: 0,
      // Derive the next offset from the actual rows we've received rather than
      // ``allPages.length * PAGE_SIZE``. If a future backend change ever returns
      // a partial non-terminal page, the multiplication shortcut would skip rows;
      // counting real rows is robust to that.
      getNextPageParam: (lastPage, allPages) =>
        lastPage.has_more ? allPages.reduce((sum, p) => sum + p.users.length, 0) : undefined,
      // ``keepPreviousData`` keeps the list stable during typeahead keystrokes
      // (smoother UX than flashing "Loading…" every 200ms). But we gate it on
      // ``open`` so a fresh reopen doesn't briefly flash the previous session's
      // accumulated pages — the observer's "last seen data" gets updated to
      // ``undefined`` during the closed render, so reopen starts clean.
      placeholderData: open ? keepPreviousData : undefined,
    })

  const allUsers = React.useMemo<UserFilterOption[]>(() => data?.pages.flatMap((p) => p.users) ?? [], [data])

  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const node = loadMoreRef.current
    // Gate on ``isPlaceholderData`` so a search-key transition (where the prior
    // query's pages are still displayed as placeholder while the new page-0
    // loads) can't trigger fetchNextPage. Without this, the observer sees the
    // sentinel still intersecting, ``allPages.reduce(...)`` sums the stale
    // pages, and the new search fetches at offset=N instead of 0 — skipping
    // matching users on the new query.
    if (!node || !hasNextPage || isFetchingNextPage || isPlaceholderData) return
    // The sentinel's direct parent is a non-scrolling wrapper div; the
    // scrolling element is the CommandList (cmdk's overflow-y-auto root).
    // Using parentElement here would make the sentinel "always intersecting"
    // because it's perpetually inside its non-scrolling parent's box —
    // which fires fetchNextPage in a loop after each page returns.
    const scrollRoot = node.closest('[data-slot="command-list"]')
    if (!scrollRoot && process.env.NODE_ENV !== 'production') {
      // If a shadcn/cmdk upgrade renames the data-slot, the observer silently
      // falls back to viewport root and the loop-fetch bug returns. Warn loudly
      // so the renaming gets caught in dev before it ships.
      console.warn('UserFilter: could not find CommandList scroll root; infinite scroll may misbehave')
    }
    // The observer re-attaches after each fetch completes (deps include
    // ``isFetchingNextPage``) so a still-intersecting sentinel chains into the
    // next page — this is the small-list auto-fill behavior, not a loop.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage()
      },
      { root: scrollRoot }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData])

  // Drop label cache entries for users that are no longer selected so the dict
  // doesn't grow without bound as the admin checks/unchecks names.
  React.useEffect(() => {
    setSelectedLabels((prev) => {
      const next: Record<string, UserFilterOption> = {}
      for (const id of value) {
        if (prev[id]) next[id] = prev[id]
      }
      return next
    })
  }, [value])

  // Predictable-width trigger label: 0 → "All users", 1 → the single user's name,
  // 2+ → "N users". Avoids the trigger pill resizing as more checkboxes flip.
  const triggerLabel: string = (() => {
    if (value.length === 0) return 'All users'
    if (value.length === 1) {
      const id = value[0]
      const fromCache = selectedLabels[id]
      if (fromCache) return renderUserLabel(fromCache)
      const fromQuery = allUsers.find((u) => u.user_id === id)
      if (fromQuery) return renderUserLabel(fromQuery)
      return `User: ${id}`
    }
    return `${value.length} users`
  })()

  const handleToggle = (user: UserFilterOption) => {
    const isSelected = value.includes(user.user_id)
    if (isSelected) {
      onChange(value.filter((id) => id !== user.user_id))
    } else {
      onChange([...value, user.user_id])
      setSelectedLabels((prev) => ({ ...prev, [user.user_id]: user }))
    }
  }

  const handleClear = () => {
    onChange([])
    setSelectedLabels({})
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOpen(false)
          // Drop accumulated infinite-scroll pages so the next open starts at page 1
          // instead of restoring however far the admin previously scrolled.
          queryClient.removeQueries({
            queryKey: ['team-insights', 'filter-options', 'users', enterpriseId],
          })
          setSearch('')
          setDebouncedSearch('')
          return
        }
        if (!disabled) setOpen(true)
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="lg" disabled={disabled} className={cn(triggerClassName)}>
          <UserIcon className="size-[22px]" />
          <span className={cn('truncate text-left', triggerClassName ? 'flex-1' : 'max-w-[160px]')}>
            {triggerLabel}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name or email" value={search} onValueChange={setSearch} />
          {value.length > 0 && (
            // Only show "Clear filter" when there's something to clear, and keep
            // it available even during load/error states so a stuck filter
            // (persistent fetch error with users already selected) can still be
            // reset from inside the popover.
            <>
              <button
                type="button"
                onClick={handleClear}
                className="hover:bg-accent flex w-full items-center px-3 py-2.5 text-left text-sm font-semibold"
              >
                Clear filter
              </button>
              <div className="bg-border mx-3 h-px" />
            </>
          )}
          <CommandList className="max-h-[420px]">
            {isLoading && <div className="text-muted-foreground py-6 text-center text-sm">Loading…</div>}
            {isError && <div className="text-muted-foreground py-6 text-center text-sm">Couldn’t load users.</div>}
            {!isLoading && !isError && (
              <div className="py-1">
                {allUsers.length === 0 && (
                  <div className="text-muted-foreground py-6 text-center text-sm">No users found.</div>
                )}
                {allUsers.map((user) => {
                  const checked = value.includes(user.user_id)
                  return (
                    <label
                      key={user.user_id}
                      className={cn(
                        'hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-sm',
                        checked && 'bg-muted'
                      )}
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{renderUserLabel(user)}</span>
                        {user.name && user.email && (
                          <span className="text-muted-foreground truncate text-xs">{user.email}</span>
                        )}
                      </div>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => handleToggle(user)}
                        aria-label={`Toggle ${renderUserLabel(user)}`}
                      />
                    </label>
                  )
                })}
                {hasNextPage && !isPlaceholderData && <div ref={loadMoreRef} className="h-1" />}
                {isFetchingNextPage && (
                  <div className="text-muted-foreground flex justify-center py-3">
                    <LoaderCircle className="size-4 animate-spin" aria-label="Loading more users" />
                  </div>
                )}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default UserFilter
